import { D9_SCALE } from '@/config/solana';
import {
  type ConsensusStopResult,
  type ConsensusStopSeries,
  stopConsensusChartAtThreshold,
} from '@/lib/chart/consensus-stop';

interface ResolutionOutcome {
  readonly outcomeIndex: number;
  readonly convictionAtResolution?: string | null;
}

/** Coarse history can omit settlement; the immutable snapshot owns its endpoint. */
export function resolveTerminalConsensusSnapshot<T extends ConsensusStopSeries>(input: {
  readonly chartData: Record<number, T>;
  readonly outcomes: readonly ResolutionOutcome[];
  readonly resolutionUnix: number;
  readonly threshold: number | undefined;
  readonly winnerOutcomeIndex: number | null;
}): ConsensusStopResult<T> & { readonly missingSnapshotOutcomeIndices: readonly number[] } {
  const chartData: Record<number, T> = {};
  const terminalValues: Record<number, number> = {};
  const missingSnapshotOutcomeIndices: number[] = [];
  const outcomesByIndex = new Map(input.outcomes.map((row) => [row.outcomeIndex, row]));
  for (const [key, entry] of Object.entries(input.chartData)) {
    const outcomeIndex = Number(key);
    const outcome = outcomesByIndex.get(outcomeIndex);
    const value = parseSnapshotFraction(outcome?.convictionAtResolution);
    if (value === undefined) {
      chartData[outcomeIndex] = entry;
      missingSnapshotOutcomeIndices.push(outcomeIndex);
      continue;
    }
    chartData[outcomeIndex] = {
      ...entry,
      line: [
        ...entry.line.filter((point) => point.time < input.resolutionUnix),
        { time: input.resolutionUnix, value },
      ],
    };
    terminalValues[outcomeIndex] = value;
  }

  // Legacy rows without a resolution snapshot retain the proven history crossing.
  if (!Object.values(terminalValues).some((value) => value > 0)) {
    return {
      ...stopConsensusChartAtThreshold({
        chartData: input.chartData,
        enabled: true,
        threshold: input.threshold,
        ...(input.winnerOutcomeIndex === null
          ? {}
          : { preferredOutcomeIndex: input.winnerOutcomeIndex }),
      }),
      missingSnapshotOutcomeIndices: Object.keys(input.chartData).map(Number),
    };
  }
  return {
    chartData,
    terminalValues,
    stopTimestamp: input.resolutionUnix,
    ...(input.winnerOutcomeIndex === null ? {} : { outcomeIndex: input.winnerOutcomeIndex }),
    missingSnapshotOutcomeIndices,
  };
}

function parseSnapshotFraction(value: string | null | undefined): number | undefined {
  if (typeof value !== 'string' || !/^\d+$/.test(value)) return undefined;
  const ppb = Number(value);
  return Number.isSafeInteger(ppb) ? ppb / D9_SCALE : undefined;
}
